import { useEffect, useMemo, useState } from 'react';

import FiltersDisclosure from '~/shared/components/filters/FiltersDisclosure';
import { SelectOption } from '~/shared/utils/types';

import type { ExplorationVolume, ViewMode } from '../../types/exploration';
import {
  buildActivationOptions,
  buildSupportFilterGroups,
} from '../../lib/filters/buildCheckboxFilterOptions';
import ActivationStatusFilter from './ActivationStatusFilter';
import CapacityRangeFilter from './CapacityRangeFilter';
import ClusterNameFilter, { buildClusterFilterOptions } from './ClusterNameFilter';
import DiskTypeFilter, { buildDiskTypeFilterOptions } from './DiskTypeFilter';
import { hasVisibleFilterOptions } from './FilterCheckboxList';
import InfrastructureFilter, { buildInfrastructureFilterOptions } from './InfrastructureFilter';
import InstanceIdFilter, { buildInstanceIdFilterOptions } from './InstanceIdFilter';
import NodeGroupFilter, { buildNodeGroupFilterOptions } from './NodeGroupFilter';
import RegionFilter, { buildRegionFilterOptions } from './RegionFilter';
import SupportStatusFilter from './SupportStatusFilter';
import VolumeIdFilter, { buildVolumeIdFilterOptions } from './VolumeIdFilter';
import VolumeTagsFilter, { buildVolumeTagFilterOptions } from './VolumeTagsFilter';
import { formatCapacityRangeBadge, isCapacityRangeNarrowed } from './utils/capacityFormat';
import type { CapacityBounds } from './utils/filterConverters';

interface FilterPanelContentProps {
  view: ViewMode;
  panelOpen?: boolean;
  filterSearchQuery?: string;
  volumes: ExplorationVolume[];
  capacityBounds: CapacityBounds;
  pendingActivationStatuses: string[];
  setPendingActivationStatuses: (statuses: string[]) => void;
  pendingSupportStatuses: string[];
  setPendingSupportStatuses: (statuses: string[]) => void;
  pendingClusterFilters: SelectOption[];
  setPendingClusterFilters: (filters: SelectOption[]) => void;
  pendingInfrastructureFilters: string[];
  setPendingInfrastructureFilters: (filters: string[]) => void;
  pendingRegionFilters: string[];
  setPendingRegionFilters: (regions: string[]) => void;
  pendingNodeGroupFilters: string[];
  setPendingNodeGroupFilters: (filters: string[]) => void;
  pendingDiskTypeFilters: string[];
  setPendingDiskTypeFilters: (filters: string[]) => void;
  pendingInstanceFilters: SelectOption[];
  setPendingInstanceFilters: (filters: SelectOption[]) => void;
  pendingVolumeIdFilters: SelectOption[];
  setPendingVolumeIdFilters: (filters: SelectOption[]) => void;
  pendingTagFilters: SelectOption[];
  setPendingTagFilters: (filters: SelectOption[]) => void;
  pendingCapacityMin: number;
  setPendingCapacityMin: (min: number) => void;
  pendingCapacityMax: number;
  setPendingCapacityMax: (max: number) => void;
}

export default function FilterPanelContent({
  view,
  panelOpen = false,
  filterSearchQuery = '',
  volumes,
  capacityBounds,
  pendingActivationStatuses,
  setPendingActivationStatuses,
  pendingSupportStatuses,
  setPendingSupportStatuses,
  pendingClusterFilters,
  setPendingClusterFilters,
  pendingInfrastructureFilters,
  setPendingInfrastructureFilters,
  pendingRegionFilters,
  setPendingRegionFilters,
  pendingNodeGroupFilters,
  setPendingNodeGroupFilters,
  pendingDiskTypeFilters,
  setPendingDiskTypeFilters,
  pendingInstanceFilters,
  setPendingInstanceFilters,
  pendingVolumeIdFilters,
  setPendingVolumeIdFilters,
  pendingTagFilters,
  setPendingTagFilters,
  pendingCapacityMin,
  setPendingCapacityMin,
  pendingCapacityMax,
  setPendingCapacityMax,
}: FilterPanelContentProps) {
  const [isActivationDisclosureOpen, setIsActivationDisclosureOpen] = useState(true);
  const [isSupportDisclosureOpen, setIsSupportDisclosureOpen] = useState(true);
  const [isInfrastructureDisclosureOpen, setIsInfrastructureDisclosureOpen] = useState(true);
  const [isClusterDisclosureOpen, setIsClusterDisclosureOpen] = useState(false);
  const [isRegionDisclosureOpen, setIsRegionDisclosureOpen] = useState(false);
  const [isNodeGroupDisclosureOpen, setIsNodeGroupDisclosureOpen] = useState(false);
  const [isDiskTypeDisclosureOpen, setIsDiskTypeDisclosureOpen] = useState(false);
  const [isCapacityDisclosureOpen, setIsCapacityDisclosureOpen] = useState(false);
  const [isVolumeIdDisclosureOpen, setIsVolumeIdDisclosureOpen] = useState(false);
  const [isInstanceDisclosureOpen, setIsInstanceDisclosureOpen] = useState(false);
  const [isTagDisclosureOpen, setIsTagDisclosureOpen] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  const sectionOptions = useMemo(
    () => ({
      activation: buildActivationOptions(volumes),
      support: buildSupportFilterGroups(volumes),
      infrastructure: buildInfrastructureFilterOptions(volumes),
      cluster: buildClusterFilterOptions(volumes),
      region: buildRegionFilterOptions(volumes),
      nodeGroup: buildNodeGroupFilterOptions(volumes),
      diskType: buildDiskTypeFilterOptions(volumes),
      volumeId: buildVolumeIdFilterOptions(volumes),
      instanceId: buildInstanceIdFilterOptions(volumes),
      tags: buildVolumeTagFilterOptions(volumes),
    }),
    [volumes]
  );

  const sectionVisible = useMemo(() => {
    const q = filterSearchQuery;
    const globalVisible = (options: { key: string; label: string; count: number }[]) =>
      !q.trim() || hasVisibleFilterOptions(options, q);

    return {
      activation: view === 'activation' && sectionOptions.activation.length > 0,
      support: view === 'exploration' && sectionOptions.support.length > 0,
      infrastructure: sectionOptions.infrastructure.length > 0,
      cluster: sectionOptions.cluster.length > 0 && globalVisible(sectionOptions.cluster),
      region: sectionOptions.region.length > 0,
      nodeGroup: sectionOptions.nodeGroup.length > 0 && globalVisible(sectionOptions.nodeGroup),
      diskType: sectionOptions.diskType.length > 0,
      volumeId: sectionOptions.volumeId.length > 0 && globalVisible(sectionOptions.volumeId),
      instanceId: sectionOptions.instanceId.length > 0 && globalVisible(sectionOptions.instanceId),
      tags: sectionOptions.tags.length > 0 && globalVisible(sectionOptions.tags),
      capacity: true,
    };
  }, [filterSearchQuery, sectionOptions, view]);

  useEffect(() => {
    if (!panelOpen) {
      setHasInitialized(false);
      return;
    }

    if (hasInitialized) return;

    if (pendingActivationStatuses.length > 0) setIsActivationDisclosureOpen(true);
    if (pendingSupportStatuses.length > 0) setIsSupportDisclosureOpen(true);
    if (pendingInfrastructureFilters.length > 0) setIsInfrastructureDisclosureOpen(true);
    if (pendingClusterFilters.length > 0) setIsClusterDisclosureOpen(true);
    if (pendingRegionFilters.length > 0) setIsRegionDisclosureOpen(true);
    if (pendingNodeGroupFilters.length > 0) setIsNodeGroupDisclosureOpen(true);
    if (pendingDiskTypeFilters.length > 0) setIsDiskTypeDisclosureOpen(true);
    if (pendingVolumeIdFilters.length > 0) setIsVolumeIdDisclosureOpen(true);
    if (pendingInstanceFilters.length > 0) setIsInstanceDisclosureOpen(true);
    if (pendingTagFilters.length > 0) setIsTagDisclosureOpen(true);
    if (isCapacityRangeNarrowed(pendingCapacityMin, pendingCapacityMax, capacityBounds)) {
      setIsCapacityDisclosureOpen(true);
    }

    setHasInitialized(true);
  }, [
    panelOpen,
    hasInitialized,
    pendingActivationStatuses.length,
    pendingSupportStatuses.length,
    pendingInfrastructureFilters.length,
    pendingClusterFilters.length,
    pendingRegionFilters.length,
    pendingNodeGroupFilters.length,
    pendingDiskTypeFilters.length,
    pendingVolumeIdFilters.length,
    pendingInstanceFilters.length,
    pendingTagFilters.length,
    pendingCapacityMin,
    pendingCapacityMax,
    capacityBounds.min,
    capacityBounds.max,
  ]);

  useEffect(() => {
    if (!filterSearchQuery.trim()) return;

    if (sectionVisible.cluster) setIsClusterDisclosureOpen(true);
    if (sectionVisible.nodeGroup) setIsNodeGroupDisclosureOpen(true);
    if (sectionVisible.volumeId) setIsVolumeIdDisclosureOpen(true);
    if (sectionVisible.instanceId) setIsInstanceDisclosureOpen(true);
    if (sectionVisible.tags) setIsTagDisclosureOpen(true);
  }, [filterSearchQuery, sectionVisible]);

  const capacityBadgeLabel = formatCapacityRangeBadge(
    pendingCapacityMin,
    pendingCapacityMax,
    capacityBounds
  );

  return (
    <div className="flex-1 overflow-y-auto pb-4">
      {sectionVisible.activation && (
        <ActivationStatusFilter
          volumes={volumes}
          open={isActivationDisclosureOpen}
          onOpenChange={setIsActivationDisclosureOpen}
          pendingActivationStatuses={pendingActivationStatuses}
          setPendingActivationStatuses={setPendingActivationStatuses}
        />
      )}

      {sectionVisible.support && (
        <SupportStatusFilter
          volumes={volumes}
          open={isSupportDisclosureOpen}
          onOpenChange={setIsSupportDisclosureOpen}
          pendingSupportStatuses={pendingSupportStatuses}
          setPendingSupportStatuses={setPendingSupportStatuses}
        />
      )}

      {sectionVisible.infrastructure && (
        <FiltersDisclosure
          title="Attached To"
          count={
            pendingInfrastructureFilters.length > 0
              ? pendingInfrastructureFilters.length
              : undefined
          }
          className="px-8 border-b border-gray-300"
          open={isInfrastructureDisclosureOpen}
          onOpenChange={setIsInfrastructureDisclosureOpen}
          onClearFilters={() => setPendingInfrastructureFilters([])}
        >
          <InfrastructureFilter
            volumes={volumes}
            pendingInfrastructureFilters={pendingInfrastructureFilters}
            setPendingInfrastructureFilters={setPendingInfrastructureFilters}
            isDisclosureOpen={isInfrastructureDisclosureOpen}
          />
        </FiltersDisclosure>
      )}

      {sectionVisible.region && (
        <FiltersDisclosure
          title="Region"
          count={pendingRegionFilters.length > 0 ? pendingRegionFilters.length : undefined}
          className="px-8 border-b border-gray-300"
          open={isRegionDisclosureOpen}
          onOpenChange={setIsRegionDisclosureOpen}
          onClearFilters={() => setPendingRegionFilters([])}
        >
          <RegionFilter
            volumes={volumes}
            pendingRegionFilters={pendingRegionFilters}
            setPendingRegionFilters={setPendingRegionFilters}
            isDisclosureOpen={isRegionDisclosureOpen}
          />
        </FiltersDisclosure>
      )}

      {sectionVisible.cluster && (
        <FiltersDisclosure
          title="Cluster"
          count={pendingClusterFilters.length > 0 ? pendingClusterFilters.length : undefined}
          className="px-8 border-b border-gray-300"
          open={isClusterDisclosureOpen}
          onOpenChange={setIsClusterDisclosureOpen}
          onClearFilters={() => setPendingClusterFilters([])}
        >
          <ClusterNameFilter
            volumes={volumes}
            pendingClusterFilters={pendingClusterFilters}
            setPendingClusterFilters={setPendingClusterFilters}
            globalSearchQuery={filterSearchQuery}
            isDisclosureOpen={isClusterDisclosureOpen}
          />
        </FiltersDisclosure>
      )}

      {sectionVisible.nodeGroup && (
        <FiltersDisclosure
          title="Node group"
          count={pendingNodeGroupFilters.length > 0 ? pendingNodeGroupFilters.length : undefined}
          className="px-8 border-b border-gray-300"
          open={isNodeGroupDisclosureOpen}
          onOpenChange={setIsNodeGroupDisclosureOpen}
          onClearFilters={() => setPendingNodeGroupFilters([])}
        >
          <NodeGroupFilter
            volumes={volumes}
            pendingNodeGroupFilters={pendingNodeGroupFilters}
            setPendingNodeGroupFilters={setPendingNodeGroupFilters}
            globalSearchQuery={filterSearchQuery}
            isDisclosureOpen={isNodeGroupDisclosureOpen}
          />
        </FiltersDisclosure>
      )}

      {sectionVisible.instanceId && (
        <FiltersDisclosure
          title="Instance ID"
          count={pendingInstanceFilters.length > 0 ? pendingInstanceFilters.length : undefined}
          className="px-8 border-b border-gray-300"
          open={isInstanceDisclosureOpen}
          onOpenChange={setIsInstanceDisclosureOpen}
          onClearFilters={() => setPendingInstanceFilters([])}
        >
          <InstanceIdFilter
            volumes={volumes}
            pendingInstanceFilters={pendingInstanceFilters}
            setPendingInstanceFilters={setPendingInstanceFilters}
            globalSearchQuery={filterSearchQuery}
            isDisclosureOpen={isInstanceDisclosureOpen}
          />
        </FiltersDisclosure>
      )}

      {sectionVisible.volumeId && (
        <FiltersDisclosure
          title="Volume ID"
          count={pendingVolumeIdFilters.length > 0 ? pendingVolumeIdFilters.length : undefined}
          className="px-8 border-b border-gray-300"
          open={isVolumeIdDisclosureOpen}
          onOpenChange={setIsVolumeIdDisclosureOpen}
          onClearFilters={() => setPendingVolumeIdFilters([])}
        >
          <VolumeIdFilter
            volumes={volumes}
            pendingVolumeIdFilters={pendingVolumeIdFilters}
            setPendingVolumeIdFilters={setPendingVolumeIdFilters}
            globalSearchQuery={filterSearchQuery}
            isDisclosureOpen={isVolumeIdDisclosureOpen}
          />
        </FiltersDisclosure>
      )}

      {sectionVisible.tags && (
        <FiltersDisclosure
          title="Volume Tags"
          count={pendingTagFilters.length > 0 ? pendingTagFilters.length : undefined}
          className="px-8 border-b border-gray-300"
          open={isTagDisclosureOpen}
          onOpenChange={setIsTagDisclosureOpen}
          onClearFilters={() => setPendingTagFilters([])}
        >
          <VolumeTagsFilter
            volumes={volumes}
            pendingTagFilters={pendingTagFilters}
            setPendingTagFilters={setPendingTagFilters}
            globalSearchQuery={filterSearchQuery}
            isDisclosureOpen={isTagDisclosureOpen}
          />
        </FiltersDisclosure>
      )}

      {sectionVisible.capacity && (
        <FiltersDisclosure
          title="Capacity"
          badgeLabel={capacityBadgeLabel}
          className="px-8 border-b border-gray-300"
          open={isCapacityDisclosureOpen}
          onOpenChange={setIsCapacityDisclosureOpen}
          onClearFilters={() => {
            setPendingCapacityMin(capacityBounds.min);
            setPendingCapacityMax(capacityBounds.max);
          }}
        >
          <CapacityRangeFilter
            bounds={capacityBounds}
            pendingCapacityMin={pendingCapacityMin}
            pendingCapacityMax={pendingCapacityMax}
            setPendingCapacityMin={setPendingCapacityMin}
            setPendingCapacityMax={setPendingCapacityMax}
            autoFocusLo={panelOpen}
          />
        </FiltersDisclosure>
      )}

      {sectionVisible.diskType && (
        <FiltersDisclosure
          title="Volume Type"
          count={pendingDiskTypeFilters.length > 0 ? pendingDiskTypeFilters.length : undefined}
          className="px-8 border-b border-gray-300"
          open={isDiskTypeDisclosureOpen}
          onOpenChange={setIsDiskTypeDisclosureOpen}
          onClearFilters={() => setPendingDiskTypeFilters([])}
        >
          <DiskTypeFilter
            volumes={volumes}
            pendingDiskTypeFilters={pendingDiskTypeFilters}
            setPendingDiskTypeFilters={setPendingDiskTypeFilters}
            isDisclosureOpen={isDiskTypeDisclosureOpen}
          />
        </FiltersDisclosure>
      )}
    </div>
  );
}
